import React, { useMemo } from "react";

import {
  Remove as RemoveIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { Box, Fab, Grid, ListItem, Theme, Typography } from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import AllergenIngredientModal from "@/components/AllergenModal/AllergenIngredientModal";
import { AllergiesKind } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { round } from "@/utils/number";

import { Ingredient } from "../store/Ingredient";

const StyledBox = withStyles(Box, (theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: theme.spacing(0, 4),
    width: "100%",
    minHeight: 56,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const StyledTypography = withStyles(Typography, (theme: Theme) => ({
  root: {
    color: theme.palette.text.primary,
    fontSize: 14,
    lineHeight: 1.25,
    fontWeight: "bold",
  },
}));

const Macronutrients = withStyles(Typography, (theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    color: colors.gray,
    fontWeight: "bold",
    fontSize: 10,
    lineHeight: 1.3,
    "& span": {
      marginRight: theme.spacing(3),
    },
  },
}));

const StyledFab = withStyles(Fab, (theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.error.main}`,
    color: theme.palette.error.main,
  },
}));

const useStyles = makeStyles()(() => ({
  allergenSpan: {
    "& .MuiSvgIcon-root": {
      width: "9.5px",
      height: "8.5px",
    },
    padding: "3px 6px 3px 6px",
    background: "#FF6262",
    color: colors.white,
    fontSize: "8px",
    borderRadius: "24px",
    marginLeft: "2px",
    cursor: "pointer",
  },
  allergenBG: {
    background: "#FFE7E7",
  },
}));

interface IngredientsListItemProps {
  ingredient: Ingredient;
  onRemoveIngredient: () => void;
  clientAllergies?: AllergiesKind[];
}

const IngredientsListItem: React.FC<IngredientsListItemProps> = ({
  ingredient,
  onRemoveIngredient,
  clientAllergies = [],
}) => {
  const { classes } = useStyles();

  const { calories, carbohydrates, proteins, fats } = useMemo(
    () => ingredient.macros,
    [ingredient]
  );

  const hasAllergies = ingredient.hasAllergies(clientAllergies);

  return (
    <ListItem disableGutters>
      <Grid container spacing={2}>
        <Grid item xs zeroMinWidth>
          <StyledBox
            style={{
              backgroundColor: hasAllergies ? "#FFE7E7" : "",
            }}
          >
            <Box display="flex" justifyContent="space-between">
              <StyledTypography noWrap>
                {ingredient.ingredient?.name}
              </StyledTypography>

              {hasAllergies ? (
                <span
                  className={classes.allergenSpan}
                  onClick={() => {
                    ingredient.showAllergenModal(true);
                  }}
                >
                  <WarningIcon /> Allergen
                </span>
              ) : (
                ""
              )}
            </Box>
            <Macronutrients variant="caption">
              <span>kcal: {round(calories, 2)}</span>
              <span>C: {displayMacro(carbohydrates)}</span>
              <span>P: {displayMacro(proteins)}</span>
              <span>F: {displayMacro(fats)}</span>
            </Macronutrients>
          </StyledBox>
        </Grid>
        <Grid item xs={2}>
          <StyledBox>
            <StyledTypography>{ingredient.quantity}</StyledTypography>
          </StyledBox>
        </Grid>
        <Grid item xs={3}>
          <StyledBox>
            <StyledTypography>
              {ingredient.portionSize?.unitWithModifier}
            </StyledTypography>
          </StyledBox>
        </Grid>
        <Grid item>
          <Box marginTop={2}>
            <StyledFab size="small" onClick={onRemoveIngredient}>
              <RemoveIcon />
            </StyledFab>
          </Box>
        </Grid>
      </Grid>

      <AllergenIngredientModal
        foodId={ingredient.ingredient?.id ?? ""}
        fromAdded={true}
        visibility={ingredient.isIngredientAllergenModalShown}
        onAddIngredient={() => {
          ingredient.showAllergenModal(false);
        }}
        onRemoveIngredient={onRemoveIngredient}
        onClose={() => ingredient.showAllergenModal(false)}
      />
    </ListItem>
  );
};

function displayMacro(macro: number | undefined) {
  if (macro == null) return "N/A";
  return `${round(macro, 2)}g`;
}

export default IngredientsListItem;
